import {
  type Carer,
  type Home,
  type IncidentReportItem,
  UserRole
} from '@lmc/ts-common'
import { EmptyState, Icon, NoDataIcon, PageHeader } from '@lmc/ui-kit'
import { Button } from '@lmc/ui-kit/src/general'
import { Pagination } from 'antd'
import { showToast } from 'common/utils'
import useSafeguardingAccess from 'components/AccessControl/useSafeguardingAccess'
import { Spinner } from 'components/generic'
import dayjs, { type Dayjs } from 'dayjs'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useFilterParamsGeneric from 'hooks/useFilterParamsGeneric'
import useHasSafeguardingEnabled from 'hooks/useHasSafeguardingEnabled'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useGenerateCsv } from 'screens/Reports/DownloadCentre/components/hooks'
import { CSV_EXPORT_TYPE } from 'screens/Reports/DownloadCentre/components/utils'
import styled from 'styled-components'
import { useIncidentsReportList } from '../hooks'
import type { FilterValues } from '../hooks/useIncidentsReportList'
import type { SetFilterParams } from '../screens/IncidentsDashboard/IncidentsDashboard'
import { IncidentFilters } from './IncidentFilters'
import { IncidentListItem } from './IncidentListItem'
import SafeguardingBanner from './SafeguardingBanner'

const initialFiltersValue: FilterValues = {
  rangePicker: [dayjs().subtract(4, 'week').startOf('day'), dayjs()],
  residents: [],
  locations: [],
  categoryItems: [],
  statuses: [],
  severities: [],
  resultedIn: []
}

export type IncidentsReportMainProps = {
  title?: string
  description?: string
  hasResidentsFilter?: boolean
  hasHomesFilter?: boolean
  hasSeverityFilter?: boolean
  hasOnClick?: boolean
  testId?: string
}

const CURRENT_PAGE = 1
const ITEMS_PER_PAGE = 10

export const IncidentsReportMain = ({
  title,
  description,
  hasResidentsFilter,
  hasHomesFilter,
  hasSeverityFilter,
  hasOnClick,
  testId = 'incidents-report-main'
}: IncidentsReportMainProps) => {
  const { filterParamsGeneric, setFilterParams } =
    useFilterParamsGeneric(initialFiltersValue)

  const filterParams = filterParamsGeneric as FilterValues

  const { data: flags } = useFeatureFlags({
    flagNames: ['safeguarding']
  })
  const isSafeguardingEnabled = flags?.safeguarding?.enabled

  const [currentPage, setCurrentPage] = useState(CURRENT_PAGE)
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE)

  const handlePageChange = (page: number, pageSize?: number) => {
    setCurrentPage(page)
    if (pageSize) {
      setItemsPerPage(pageSize)
    }
  }

  const { data, isLoading, refetch } = useIncidentsReportList({
    ...filterParams,
    page: currentPage,
    itemsPerPage
  })

  const incidents = data?.data || []
  const pagination = data?.pagination || {}

  useEffect(() => {
    refetch()
  }, [])

  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)

  const homes = useSelector(
    (state: { data: { homes: Home[] } }) => state.data.homes
  )

  const user = useSelector(
    (state: { global: { user: Carer } }) => state.global.user
  )

  const hasIncidents = !!incidents?.length

  const { isLoading: isCSVLoading, generate } = useGenerateCsv(
    CSV_EXPORT_TYPE.INCIDENT_REPORTS,
    () => {
      showToast({
        title: `${t(incidentReportsConstants.EMAIL_SENT_TOAST_1)} ${
          user.email
        }. ${t(incidentReportsConstants.EMAIL_SENT_TOAST_2)}`
      })
    }
  )

  const isGroupUser = [UserRole.groupAdmin, UserRole.groupManager]?.includes(
    user?.role
  )

  const getRangePicker = (
    rangePicker: [Dayjs | null, Dayjs | null] | undefined | null
  ) =>
    rangePicker && rangePicker[0] && rangePicker[1]
      ? [
          dayjs(rangePicker[0]).endOf('day').toISOString(),
          dayjs(rangePicker[1]).endOf('day').toISOString()
        ]
      : undefined

  const csvExportFilters = {
    ...filterParamsGeneric,
    rangePicker: getRangePicker(filterParamsGeneric.rangePicker),
    ...(isGroupUser && {
      sites: filterParamsGeneric.homes
        ? filterParamsGeneric.homes
        : homes?.map((home) => home.id)
    })
  }

  const safeguardingAccess = useSafeguardingAccess()
  const hasSafeguardingHook = useHasSafeguardingEnabled()

  const hasSafeguardingEnabled =
    isSafeguardingEnabled && (isGroupUser || hasSafeguardingHook)

  const displaySafeguardingBanner =
    hasSafeguardingEnabled && !safeguardingAccess

  return (
    <>
      <PageHeader
        testId={`${testId}`}
        title={
          <TitleContainer data-testid={`${testId}-title-container`}>
            {title}
          </TitleContainer>
        }
        subtitle={description}
      >
        <Button
          variant="default"
          onClick={() => generate(csvExportFilters)}
          data-testid={`${testId}-export-csv-button`}
        >
          <Icon name="DownloadOutlined" />
          <span>{`${
            isCSVLoading
              ? t(incidentReportsConstants.DOWNLOADING)
              : t(incidentReportsConstants.EXPORT_CSV)
          }`}</span>
        </Button>
      </PageHeader>
      <IncidentFilters
        data-testid={`${testId}-filters`}
        filterParams={filterParams}
        setFilterParams={setFilterParams as SetFilterParams}
        hasResidentsFilter={hasResidentsFilter}
        hasHomesFilter={hasHomesFilter}
        hasSeverityFilter={hasSeverityFilter}
      />
      {displaySafeguardingBanner && <SafeguardingBanner />}
      <ListItemContainer data-testid={`${testId}-list-container`}>
        {!isLoading &&
          hasIncidents &&
          incidents?.map((item: IncidentReportItem, index: number) => (
            <IncidentListItem
              // @ts-ignore
              key={item.log._id}
              data-testid={`${testId}-list-item`}
              incident={item}
              homes={hasHomesFilter ? homes : undefined}
              hasOnClick={hasOnClick}
              index={index}
            />
          ))}
      </ListItemContainer>
      {!isLoading && !hasIncidents && (
        <EmptyState
          data-testid={`${testId}-empty-state`}
          title="Shoot... no data to show you"
          icon={<NoDataIcon />}
        />
      )}
      {isLoading && <Spinner data-testid={`${testId}-spinner`} />}
      <Pagination
        data-testid={`${testId}-pagination`}
        current={currentPage}
        pageSize={itemsPerPage}
        total={pagination.totalItems || 0}
        onChange={handlePageChange}
        showSizeChanger
        pageSizeOptions={['10', '20', '50']}
        style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}
      />
    </>
  )
}

const ListItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`
